// NewsletterHistory.js
import { useEffect, useState } from "react";
import Api from "../../../../shared/api/api";
import "../styles/AdminNewsletterHistory.css";

const PAGE_SIZE = 10;

function formatDate(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  } catch {
    return value;
  }
}

export default function NewsletterHistory() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [reconcile, setReconcile] = useState(null);
  const [busy, setBusy] = useState(false);

  const pageSize = PAGE_SIZE;
  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 1;

  useEffect(() => {
    fetchHistory(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function fetchHistory(nextPage = 1) {
    setLoading(true);
    setError("");
    try {
      const data = await Api.getNewsletterHistory(nextPage, pageSize);

      // allow a few response shapes
      const list =
        data.newsletters ||
        data.items ||
        data.rows ||
        (Array.isArray(data) ? data : []);

      setItems(list);
      setTotal(data.total || list.length || 0);
    } catch (err) {
      console.error("Error loading newsletter history:", err);
      setError("Failed to load newsletter history.");
    } finally {
      setLoading(false);
    }
  }

  async function handleView(id) {
    try {
      const data = await Api.getNewsletterById(id);
      setSelected(data);
      setModalOpen(true);
    } catch (err) {
      console.error("Error loading newsletter:", err);
      setError("Failed to load newsletter.");
    }
  }

  async function handleReconcile(event) {
    event.preventDefault();
    setBusy(true); setError("");
    try {
      await Api.request(`newsletter/history/${reconcile.id}/reconcile`, { status: reconcile.status, recipients: Number(reconcile.recipients), evidence: reconcile.evidence, confirmed: reconcile.confirmed }, "post");
      setReconcile(null);
      await fetchHistory(page);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this newsletter record? This cannot be undone.")) {
      return;
    }
    try {
      await Api.deleteNewsletterRecord(id);
      setItems((prev) => prev.filter((n) => n.id !== id));
      setTotal((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error("Error deleting newsletter:", err);
      alert("Failed to delete newsletter record.");
    }
  }

  return (
    <div className="adminPageContainer newsletter-history">
      <div className="adminCard">
        <div className="adminHeaderRow">
          <h1>Newsletter History</h1>
        </div>

        {error && <div className="adminError">{error}</div>}
        {loading && <div className="adminLoading">Loading...</div>}
        {reconcile && <form onSubmit={handleReconcile} className="newsletter-reconcile"><h2>Reconcile Newsletter {reconcile.id}</h2><label>Verified outcome<select value={reconcile.status} onChange={event => setReconcile(value => ({ ...value, status: event.target.value }))}><option value="sent">Accepted by provider</option><option value="failed">Failed or incomplete</option></select></label><label>Provider-accepted recipients<input required type="number" min="0" max={reconcile.target} value={reconcile.recipients} onChange={event => setReconcile(value => ({ ...value, recipients: event.target.value }))} /></label><label>Verification evidence<textarea required minLength={20} maxLength={2000} value={reconcile.evidence} onChange={event => setReconcile(value => ({ ...value, evidence: event.target.value }))} /></label><label><input required type="checkbox" checked={reconcile.confirmed} onChange={event => setReconcile(value => ({ ...value, confirmed: event.target.checked }))} />I verified these results in provider records.</label><button type="submit" disabled={busy}>Record verified status</button><button type="button" disabled={busy} onClick={() => setReconcile(null)}>Cancel</button></form>}

        {!loading && (
          <div className="adminTableWrapper">
            <table className="adminTable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Subject</th>
                  <th>Scheduled At</th>
                  <th>Sent At</th>
                  <th>Status</th>
                  <th>Recipients</th>
                  <th>Error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="adminTableEmpty">
                      No newsletters found.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>{item.subject}</td>
                      <td>{formatDate(item.scheduled_at)}</td>
                      <td>{formatDate(item.sent_at)}</td>
                      <td className={`status-${item.status || "unknown"}`}>
                        {item.needs_reconciliation ? "Needs reconciliation" : item.status || "unknown"}
                      </td>
                      <td>{item.recipients ?? ""}</td>
                      <td className="errorCell">
                        {item.error_message || ""}
                      </td>
                      <td>
                        <div className="adminTableActions">
                          <button
                            type="button"
                            className="btnSecondary"
                            onClick={() => handleView(item.id)}
                          >
                            View
                          </button>
                          {item.needs_reconciliation ? <button
                            type="button"
                            className="btnPrimary"
                            onClick={() => setReconcile({ id: item.id, target: item.recipients, recipients: "", status: "sent", evidence: "", confirmed: false })}
                          >
                            Reconcile
                          </button> : null}
                          <button
                            type="button"
                            className="btnDanger"
                            onClick={() => handleDelete(item.id)}
                            disabled={busy || ["scheduled", "sending"].includes(item.status)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="adminPagination">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
            >
              Prev
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {modalOpen && selected && (
        <div className="adminModalOverlay" onClick={() => setModalOpen(false)}>
          <div className="adminModal" onClick={(e) => e.stopPropagation()}>
            <div className="adminModalHeader">
              <h2>{selected.subject || "Newsletter"}</h2>
              <button
                type="button"
                className="adminModalClose"
                onClick={() => setModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="adminModalBody">
              {selected.body_html ? (
                <iframe title="Newsletter preview" sandbox="" srcDoc={selected.body_html}
                  className="newsletterPreview"
                />
              ) : (
                <pre className="newsletterPreviewText">
                  {selected.body_text || "(No content)"}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
